/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';

import Select, { components } from '@atlaskit/select';

import {
  ProjectIconStyled,
  ProjectOptionStyled,
} from '@qality/common-components/src/select/RecentAndMoreProjectSelect.styled';
import { SelectHint } from '@qality/common-components/src/select/select-components/SelectHint';
import {
  ValueContainerWithOverflow,
  ValueContainerWithOverflowStyles,
} from '@qality/common-components/src/select/select-components/ValueContainerWithOverflow';
import { ProjectSelectOption } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';

import {
  addProjectFromUrl,
  addProjectFromValue,
  mapProjectsToSelectOptions,
} from './RecentAndMoreProjectSelect.util';

type Props = {
  onChange: (option: ProjectSelectOption) => Promise<void>;
  isClearable: boolean;
  value?: ProjectSelectOption;
  isDisabled: boolean;
  placeholder?: string;
  isInvalid: boolean;
  projectId?: string;
  isInModal: boolean;
  isMulti?: boolean;
  optionsLimit?: number;
};

const ERROR_MESSAGE = [
  'We could not retrieve project details.',
  'Please try again. If the problem persists, feel free to contact our support.',
].join(' ');

export default function TestCycleProjectSelect({
  onChange,
  isClearable,
  value,
  isDisabled = false,
  isInvalid,
  placeholder,
  projectId,
  isInModal,
  isMulti,
}: Props): JSX.Element {
  const [selectOptions, setSelectOptions] = useState<
    Array<ProjectSelectOption>
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initOptions();
  }, []);

  const initOptions = async (): Promise<void> => {
    const params = {
      filterByQAlitySettings: true,
    };

    try {
      const projectsFromAPI = await StorageOfUniqueServices.get(
        'ProjectServiceUnique'
      ).getProjectsPaginated(params);

      const mappedProjects = mapProjectsToSelectOptions(projectsFromAPI);

      if (value) {
        const projects = addProjectFromValue(mappedProjects, value);
        setSelectOptions(projects);
      } else if (projectId === 'ALL_PROJECTS') {
        await onChange({ label: 'All projects', value: 'ALL_PROJECTS' });
        setSelectOptions(mappedProjects);
      } else if (projectId) {
        const { projects, project } = await addProjectFromUrl(
          mappedProjects,
          projectId
        );

        setSelectOptions(projects);
        await onChange(project);
      } else {
        setSelectOptions(mappedProjects);
      }
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Select
      isDisabled={isDisabled}
      isClearable={isClearable}
      className='project-select'
      classNamePrefix='solkit'
      options={selectOptions}
      placeholder={placeholder}
      onChange={onChange}
      value={value}
      validationState={isInvalid ? 'error' : undefined}
      isLoading={isLoading}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      formatOptionLabel={(option: ProjectSelectOption) => {
        return (
          <ProjectOptionStyled>
            {option.iconUrl && <ProjectIconStyled src={option?.iconUrl} />}
            {option.label}
          </ProjectOptionStyled>
        );
      }}
      components={{
        ValueContainer: ValueContainerWithOverflow,

        MenuList: (props) => {
          return (
            <components.MenuList {...props}>
              <SelectHint text='Use textfield to search for project' />
              {props!.children}
            </components.MenuList>
          );
        },
      }}
      menuPortalTarget={isInModal && document.body}
      styles={{
        multiValue: (base) => ({
          ...base,
          ...ValueContainerWithOverflowStyles,
        }),
        menuPortal: (base) => {
          if (isInModal) {
            return {
              ...base,
              zIndex: 9999,
              transform: 'translate(-10px)',
            };
          }
          return base;
        },
      }}
    />
  );
}
